import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { SatelliteLock } from 'src/app/core/interfaces/satelliteLock';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { Utils } from 'src/app/core/resources/utils';
import { SatelliteLockService } from 'src/app/modules/satellite-lock/satellite-lock.service';

@Component({
  selector: 'app-autocomplete-satellite-lock',
  templateUrl: './autocomplete-satellite-lock.component.html',
  styleUrls: ['./autocomplete-satellite-lock.component.scss']
})
export class AutocompleteSatelliteLockComponent implements OnInit {

  @Input() options: OptionsAutocomplete;
  @Input() inputFormControl: FormControl;
  @Input() validate: string = '';
  loadingSatelliteLocks: boolean = false;

  formControlSatelliteLock: FormControl = new FormControl();
  listSatelliteLocks: SatelliteLock[] = [];

  private initialLoadDone: boolean = false;
  constructor(
    private satelliteLockService: SatelliteLockService,
    public utils: Utils
  ) { }

  ngOnInit() {
    const validator = this.inputFormControl && this.inputFormControl.validator ? this.inputFormControl.validator({} as AbstractControl) : '';
    if (validator && validator.required) this.formControlSatelliteLock.setValidators(Validators.required);
    setTimeout(() => {
      if (!this.initialLoadDone) {
        this.getSatelliteLocks();
        this.initialLoadDone = true;
      }
    }, 0);
  }

  async getSatelliteLocks() {
    this.loadingSatelliteLocks = true;
    try {
      let currentSatelliteLockList: SatelliteLock[] = [];
      if (this.options.filterByTypeName && this.options.filterByTypeName.trim() !== '') {
        currentSatelliteLockList = await this.satelliteLockService
          .getSatelliteLocksByTypeName(this.options.filterByTypeName, this.options.pageSize)
          .toPromise();
      } else {
        const filters = this.options.filterByType ? `&type=${this.options.filterByType}` : undefined;
        const response = await this.satelliteLockService.getSatelliteLocks(this.options.pageSize, undefined, filters).toPromise();
        currentSatelliteLockList = response.items || [];
      }

      this.loadingSatelliteLocks = false;
      this.listSatelliteLocks = currentSatelliteLockList.length ? currentSatelliteLockList : [];
    } catch (error) {
      this.loadingSatelliteLocks = false;
      this.listSatelliteLocks = [];
      console.error('Error loading satellite locks for autocomplete:', error);
    }
  }

  displaySatelliteLockName(satelliteLock?: any): string | undefined {
    if (!satelliteLock) return undefined;
    return satelliteLock.imei || satelliteLock.id || satelliteLock.consecutive || undefined;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      if (changes.options && changes.options.currentValue) {
        if (changes.options.currentValue.initialValue || changes.options.currentValue.initialValue === "") {
          changes.options.currentValue.initialValue
            ? this.setInitialSatelliteLock(changes.options.currentValue.initialValue)
            : this.formControlSatelliteLock.setValue("");
        }
      }

      if (changes.filterByType || changes.filterByTypeName) {
        const hasValidTypeName = this.options.filterByTypeName && this.options.filterByTypeName.trim() !== '';
        const hasValidTypeId = this.options.filterByType && this.options.filterByType.trim() !== '';

        if (hasValidTypeName || hasValidTypeId) {
          this.initialLoadDone = true;
          this.getSatelliteLocks();
        }
      }

      if (changes.validate) {
        switch (this.validate) {
          case 'touched':
            this.formControlSatelliteLock.markAsTouched();
            break;
          case 'untouched':
            this.formControlSatelliteLock.markAsUntouched();
            break;
          case 'enable':
            this.formControlSatelliteLock.enable();
            break;
          case 'disable':
            this.formControlSatelliteLock.disable();
            break;
          case 'disable&untouched':
            this.formControlSatelliteLock.markAsUntouched();
            this.formControlSatelliteLock.disable();
            break;
          case 'setValidators':
            this.formControlSatelliteLock.setValidators(Validators.required);
            this.formControlSatelliteLock.updateValueAndValidity();
            break;
          case 'clearValidators':
            this.formControlSatelliteLock.clearValidators();
            this.formControlSatelliteLock.updateValueAndValidity();
            break;
          default:
            break;
        }
      }
    }
  }

  async setInitialSatelliteLock(id: string) {
    if (!this.listSatelliteLocks.length) await this.getSatelliteLocks();
    const satelliteLock = this.listSatelliteLocks.find((satelliteLock) => satelliteLock.id === id.toString() || satelliteLock.documentId === id.toString());
    if (satelliteLock) {
      this.formControlSatelliteLock.setValue(satelliteLock);
      this.inputFormControl.setValue(satelliteLock);
    }
  }

  onSelectSatelliteLock($event: MatAutocompleteSelectedEvent) {
    if ($event && $event.option && $event.option.value && this.inputFormControl) this.inputFormControl.setValue($event.option.value);
  }

  changeValue() {
    if (this.inputFormControl) this.inputFormControl.setValue("");
  }

  get satelliteLocksFiltered(): SatelliteLock[] {
    const value: SatelliteLock | string = this.formControlSatelliteLock.value
    if (!value) return this.listSatelliteLocks;
    const term = typeof value === 'string' ? value : (value.id || value.consecutive || value.imei || '');
    return this.listSatelliteLocks.filter(lock => lock && (
      (lock.id && lock.id.toLowerCase().includes(term.toLowerCase())) ||
      (lock.consecutive && lock.consecutive.toLowerCase().includes(term.toLowerCase())) ||
      (lock.imei && lock.imei.toLowerCase().includes(term.toLowerCase()))
    ));
  }
}
